import { createReducer } from '@reduxjs/toolkit';
import {
  hydrateProducts,
  hydrateServices,
  hydratePrintMaterials,
  hydrateCustomers,
  hydrateVendors,
  hydratePurchases,
  hydrateExpenses,
  hydratePaymentMethods,
  hydrateSales,
  hydrateCredits,
  hydrateDueCollections,
  saveProduct,
  saveService,
  savePrintMaterial,
  removePrintMaterial,
  saveCustomer,
  saveVendor,
  saveStock,
  cancelSale,
  settleCredit,
  addPaymentMethod,
  removePaymentMethod,
  completeSale,
  recordPurchase,
  payPurchase,
  cancelPurchase,
  setDateFormat,
  initializeSeed,
} from './actions';
import { EMPTY_DATA } from '@/lib/seed';
import { createId, nextInvoiceNumber, updateProductStock, updateMaterialStock, makeCredit } from '@/lib/store';
import type { AppData, Product, Service, PrintMaterial, Customer, Vendor, Purchase, Credit, Sale, SaleItem, PurchaseItem } from '@/lib/types';

export interface PosDataState extends AppData {}

export const initialState: PosDataState = EMPTY_DATA;

const posDataReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(initializeSeed, (state, action) => action.payload.data)
    .addCase(hydrateProducts, (state, action) => {
      state.products = action.payload.products;
    })
    .addCase(hydrateServices, (state, action) => {
      state.services = action.payload.services;
    })
    .addCase(hydratePrintMaterials, (state, action) => {
      state.printMaterials = action.payload.materials;
    })
    .addCase(hydrateCustomers, (state, action) => {
      state.customers = action.payload.customers;
    })
    .addCase(hydrateVendors, (state, action) => {
      state.vendors = action.payload.vendors;
    })
    .addCase(hydratePurchases, (state, action) => {
      state.purchases = action.payload.purchases;
    })
    .addCase(hydrateExpenses, (state, action) => {
      state.expenses = action.payload.expenses;
    })
    .addCase(hydratePaymentMethods, (state, action) => {
      state.paymentMethods = action.payload.paymentMethods;
    })
    .addCase(hydrateSales, (state, action) => {
      state.sales = action.payload.sales;
    })
    .addCase(hydrateCredits, (state, action) => {
      state.credits = action.payload.credits;
    })
    .addCase(hydrateDueCollections, (state, action) => {
      state.dueCollections = action.payload.dueCollections;
    })
    .addCase(saveProduct, (state, action) => {
      const { product, isNew } = action.payload;
      if (isNew) {
        state.products.unshift(product);
        state.stockMovements.unshift({
          id: createId('movement'),
          productId: product.id,
          type: 'opening',
          quantityChange: product.stockQuantity,
          resultingQuantity: product.stockQuantity,
          reason: 'Opening stock',
          createdAt: new Date().toISOString(),
        });
      } else {
        state.products = state.products.map(item => item.id === product.id ? product : item);
      }
    })
    .addCase(saveService, (state, action) => {
      const { service, isNew } = action.payload;
      if (isNew) {
        state.services.unshift(service);
      } else {
        state.services = state.services.map(item => item.id === service.id ? service : item);
      }
    })
    .addCase(savePrintMaterial, (state, action) => {
      const { material, isNew } = action.payload;
      if (isNew) {
        state.printMaterials.unshift(material);
      } else {
        state.printMaterials = state.printMaterials.map(item => item.id === material.id ? material : item);
      }
    })
    .addCase(removePrintMaterial, (state, action) => {
      const { id } = action.payload;
      state.printMaterials = state.printMaterials.filter(item => item.id !== id);
      state.services = state.services.map(service => ({
        ...service,
        rates: service.rates?.filter(rate => rate.materialId !== id),
      }));
    })
    .addCase(saveCustomer, (state, action) => {
      const { customer, isNew } = action.payload;
      if (isNew) {
        state.customers.unshift(customer);
      } else {
        state.customers = state.customers.map(item => item.id === customer.id ? customer : item);
      }
    })
    .addCase(saveVendor, (state, action) => {
      const { vendor, isNew } = action.payload;
      if (isNew) {
        state.vendors.unshift(vendor);
      } else {
        state.vendors = state.vendors.map(item => item.id === vendor.id ? vendor : item);
      }
    })
    .addCase(saveStock, (state, action) => {
      const { productId, mode, quantity, reason } = action.payload;
      const product = state.products.find(item => item.id === productId);
      if (!product) return;
      const change = mode === 'add' ? Math.max(0, quantity) : quantity - product.stockQuantity;
      const newQty = Math.max(0, product.stockQuantity + change);
      product.stockQuantity = newQty;
      state.stockMovements.unshift({
        id: createId('movement'),
        productId,
        type: mode === 'add' ? 'purchase' : 'adjusted',
        quantityChange: change,
        resultingQuantity: newQty,
        reason,
        createdAt: new Date().toISOString(),
      });
    })
    .addCase(cancelSale, (state, action) => {
      const { sale } = action.payload;
      const target = state.sales.find(item => item.id === sale.id);
      if (target) {
        target.status = 'cancelled';
        target.cancelledAt = new Date().toISOString();
        target.items
          .filter(item => item.itemType === 'product' && item.productId)
          .forEach(item => updateProductStock(state, item.productId!, item.quantity, 'returned', `Sale ${sale.invoiceNumber} cancelled`));
        state.credits = state.credits.filter(
          credit => !(credit.saleId === sale.id && credit.status === 'outstanding')
        );
      }
    })
    .addCase(settleCredit, (state, action) => {
      const { credit } = action.payload;
      const target = state.credits.find(item => item.id === credit.id);
      if (target) {
        target.status = 'settled';
        target.settledAt = new Date().toISOString();
      }
    })
    .addCase(addPaymentMethod, (state, action) => {
      const { name } = action.payload;
      if (!state.paymentMethods.some(method => method.name.toLowerCase() === name.toLowerCase())) {
        state.paymentMethods.push({ id: createId('payment'), name });
      }
    })
    .addCase(removePaymentMethod, (state, action) => {
      const { id } = action.payload;
      state.paymentMethods = state.paymentMethods.filter(item => item.id !== id);
    })
    .addCase(completeSale, (state, action) => {
      const { cart, paymentMethod, total, customerId, payNow, customerName, customerType } = action.payload;
      const paid = Math.max(0, payNow);
      const sale: Sale = {
        id: createId('sale'),
        invoiceNumber: nextInvoiceNumber(state),
        status: 'completed',
        totalAmount: total,
        discountAmount: 0,
        paymentMethod,
        customerId,
        customerName,
        customerType,
        paidAmount: paid,
        createdAt: new Date().toISOString(),
        items: cart,
      };
      state.sales.unshift(sale);
      cart
        .filter(item => item.itemType === 'product' && item.productId)
        .forEach(item => updateProductStock(state, item.productId!, item.quantity, 'sold', `Sale ${sale.invoiceNumber}`));
      const overpayment = Math.max(0, paid - total);
      // Advances only — dues live as credit rows (see below).
      if (overpayment > 0 && customerId) {
        const customer = state.customers.find(item => item.id === customerId);
        if (customer) {
          customer.creditBalance = Number(customer.creditBalance || 0) - overpayment;
        }
      }
      if (total - Math.min(total, paid) > 0 && customerId) {
        const customer = state.customers.find(item => item.id === customerId);
        if (customer) {
          state.credits.unshift(makeCredit(sale, cart, customer, Math.min(total, paid)));
        }
      }
    })
    .addCase(recordPurchase, (state, action) => {
      const { vendorId, items, paidAmount } = action.payload;
      const vendor = state.vendors.find(item => item.id === vendorId);
      const totalAmount = items.reduce((sum, item) => sum + item.lineTotal, 0);
      const paid = Math.min(totalAmount, Math.max(0, paidAmount));
      const record: Purchase = {
        id: createId('purchase'),
        vendorId: vendor?.id,
        vendorName: vendor?.name || 'Walk-in supplier',
        items,
        totalAmount,
        paidAmount: paid,
        dueAmount: totalAmount - paid,
        paymentStatus: paid >= totalAmount ? 'paid' : paid > 0 ? 'partial' : 'unpaid',
        status: 'completed',
        createdAt: new Date().toISOString(),
      };
      state.purchases.unshift(record);
      items.forEach(item => {
        if (item.productId) {
          updateProductStock(state, item.productId, item.quantity, 'purchase', `Purchase from ${record.vendorName}`);
        } else if ((item as any).materialId) {
          updateMaterialStock(state, (item as any).materialId, item.quantity);
        }
      });
    })
    .addCase(payPurchase, (state, action) => {
      const { purchaseId, amount } = action.payload;
      const purchase = state.purchases.find(item => item.id === purchaseId);
      if (!purchase) return;
      const payment = Math.min(purchase.dueAmount, Math.max(0, amount));
      purchase.paidAmount += payment;
      purchase.dueAmount = Math.max(0, purchase.dueAmount - payment);
      purchase.paymentStatus =
        purchase.dueAmount <= 0
          ? 'paid'
          : purchase.paidAmount > 0
            ? 'partial'
            : 'unpaid';
      if (purchase.dueAmount <= 0) {
        purchase.settledAt = new Date().toISOString();
      }
    })
    .addCase(cancelPurchase, (state, action) => {
      const { purchase } = action.payload;
      const target = state.purchases.find(item => item.id === purchase.id);
      if (target) {
        target.status = 'cancelled';
        target.items.forEach(item => {
          if (item.productId) {
            updateProductStock(state, item.productId, -item.quantity, 'returned', `Purchase ${target.id} cancelled`);
          } else if ((item as any).materialId) {
            updateMaterialStock(state, (item as any).materialId, -item.quantity);
          }
        });
      }
    })
    .addCase(setDateFormat, (state, action) => {
      state.dateFormat = action.payload.format;
    });
});

export default posDataReducer;
